import * as Yup from 'yup';
import { differenceInYears } from 'date-fns';
import {
  driverCarAssignmentStates,
  UMPDOnlyWithUMBIStates,
  combinedUMUIM,
  doNotShowCarLevelUMPDStates,
  policyLevelUMPDStates,
  policyLevelUMPDInUMSelectionStates,
  inexperiencedYearsLimit,
  canRejectPIPStates,
  umpdWithCollisionStates
} from '@ourbranch/lookups';

import { hasNewCoApplicant } from 'customer/components/policy/details/helpers';
import { getPolicyStatus, PolicyStatus } from 'core/helpers/policy-status';
import { haveAnyOfThisCarsSymbolsChanged, hasMakeModelStyleSymbols } from 'common/helpers/car-symbols-helpers';
import { validUSState, requiredString, addDriverNameToError } from 'common/helpers/yup-helpers';
import { isLimitACPEValid, getUMPDValue, getPLimitValue } from 'core/helpers/car-validations';
import { driversLicenseNumberSchema } from 'core/helpers/drivers-license-number-validations';
import { checkZipCodeRange } from 'common/helpers/address-helpers';

Yup.addMethod(Yup.string, 'requiredString', requiredString);
Yup.addMethod(Yup.mixed, 'validUSState', validUSState);
Yup.addMethod(Yup.mixed, 'addDriverNameToError', addDriverNameToError);

export const autoValidationSchema = ({ policies, policy, session }) =>
  Yup.lazy(({ autoCoverage }) => {
    const skipUmbrellaBIPDValidation = policy?.overrides?.overridesForFeFormValidation?.skipUmbrellaBIPDValidation;

    // Helper function
    // Pick the home policy whose status best aligns with the auto policy we're validating:
    // prefer one matching the auto's own status (e.g., future auto → future home), then fall
    // back to Active → InCancellation → Future. This avoids matching an unrelated home policy
    // (e.g., cancelled or inactive) when multiple exist on the account.
    const getMatchedHomePolicy = () => {
      const autoStatus = policy ? getPolicyStatus(policy) : undefined;
      const homePolicies = (policies || []).filter((p) => p.policyType === 'H');
      const findHomeByStatus = (status) => homePolicies.find((h) => getPolicyStatus(h) === status);
      return (
        findHomeByStatus(autoStatus) ||
        findHomeByStatus(PolicyStatus.Active) ||
        findHomeByStatus(PolicyStatus.InCancellation) ||
        findHomeByStatus(PolicyStatus.Future)
      );
    };

    return Yup.object().shape({
      autoCoverage: Yup.object().shape({
        policyLimitBIPD: Yup.string().test(
          'policyLimitBIPD',
          'Error, umbrella is active on Home policy. BIPD needs to be at least $500K per occurrence',
          (value) => {
            if (skipUmbrellaBIPDValidation) {
              return true;
            }
            if (value) {
              const matchedHomePolicy = getMatchedHomePolicy();
              if (matchedHomePolicy && matchedHomePolicy.policyDetails.includeUmbrella) {
                const policyLimitBIPDParts = value.split('/');
                if (policyLimitBIPDParts.length === 3) {
                  const perOccurrence = parseInt(policyLimitBIPDParts[1]);
                  return perOccurrence >= 500;
                }
                return value.includes('500 CSL') || value.includes('300 CSL');
              }
            }

            return true;
          }
        ),
        policyLimitMedicalPayments: Yup.string()
          .test('medicalPaymentsOrComboFBP', 'Medical payments limit is required', function (value) {
            if (this.options.context?.state === 'PA') {
              const { policyLimitComboFBP } = this.parent;
              if (policyLimitComboFBP && policyLimitComboFBP !== 'NONE' && value !== 'NONE') {
                return this.createError({
                  message: 'Can not choose both this and combined first party benefits'
                });
              }
              if (policyLimitComboFBP && policyLimitComboFBP === 'NONE' && value === 'NONE') {
                return this.createError({
                  message: 'Choose medical payments or combined first party benefits'
                });
              }
            }
            return true;
          })
          .test('medicalPaymentsOrPIPNotBothTX', 'Cannot choose both medical payments and PIP in TX', function (value) {
            if (this.options.context?.state === 'TX') {
              const { policyLimitPIP } = this.parent;
              if (value !== 'NONE' && policyLimitPIP && policyLimitPIP !== 'NONE') {
                return false;
              }
              return true;
            }
            return true;
          })
          .nullable(),
        policyLimitComboFBP: Yup.string()
          .test('medicalPaymentsOrComboFBP', 'Combined first party benefits limit is required', function (value) {
            if (this.options.context?.state === 'PA') {
              const { policyLimitMedicalPayments } = this.parent;
              if (policyLimitMedicalPayments && policyLimitMedicalPayments !== 'NONE' && value !== 'NONE') {
                return this.createError({
                  message: 'Can not choose both this and medical payments'
                });
              }
              if (policyLimitMedicalPayments && policyLimitMedicalPayments === 'NONE' && value === 'NONE') {
                return this.createError({
                  message: 'Choose medical payments or combined first party benefits'
                });
              }
            }
            return true;
          })
          .nullable(),
        policyLimitPIP: Yup.string()
          .test('medicalPaymentsOrPIPNotBothTX', 'Cannot choose both PIP and medical payments in TX', function (value) {
            if (this.options.context?.state === 'TX') {
              const { policyLimitMedicalPayments } = this.parent;
              if (value !== 'NONE' && policyLimitMedicalPayments && policyLimitMedicalPayments !== 'NONE') {
                return false;
              }
              return true;
            }
            return true;
          })
          .test(
            'policyLimitNoFaultPIPIncluded',
            'You must reject PIP coverage in order to opt out of PIP',
            function (value) {
              if (canRejectPIPStates.includes(this.options.context?.state)) {
                const { policyLimitNoFaultPIP } = this.parent;
                if (value === 'NONE' && policyLimitNoFaultPIP && policyLimitNoFaultPIP === 'INCLUDED') {
                  return false;
                }
                return true;
              }
              return true;
            }
          )
          .test(
            'policyLimitNoFaultPIPNone',
            'PIP coverage must be set to "No Coverage" if PIP is rejected',
            function (value) {
              if (canRejectPIPStates.includes(this.options.context?.state)) {
                const { policyLimitNoFaultPIP } = this.parent;
                if (value !== 'NONE' && policyLimitNoFaultPIP && policyLimitNoFaultPIP === 'NONE') {
                  return false;
                }
                return true;
              }
              return true;
            }
          )
          .test(
            'numberOfHouseholdMembersMustbeGreaterThan2ToHaveAllApplicants',
            'PIP Deductible must be applied to “Applicant Only” if there is only 1 household resident',
            function (value) {
              if (this.options.context.state === 'FL') {
                const { householdMembers } = this.options.context.auto;

                if (!value.includes('NI') && householdMembers === 1) {
                  return false;
                }
                return true;
              }
              return true;
            }
          )
          .nullable(),
        policyLimitUMBI: Yup.string()
          .test('umbiCSL', 'UM/UIM BI cannot be CSL unless policy BI is also CSL.', function () {
            const { policyLimitUMBI, policyLimitBIPD } = this.options.context?.autoCoverage || {};
            const hasAutoPolicy = this.options.context?.auto;
            if (!hasAutoPolicy) {
              return true;
            }
            const umbiCSL = policyLimitUMBI?.includes('CSL');
            const bipdCSL = policyLimitBIPD?.includes('CSL');

            return policyLimitUMBI === '0/0' || umbiCSL === bipdCSL;
          })
          .test('FL_UM/UIMBI_must_match_BIPD', 'UM/UIMBI must match BIPD limits', function (val) {
            const { policyLimitBIPD } = this.options.context?.autoCoverage || {};
            if (this.options.context.state !== 'FL' || val === '0/0') {
              return true;
            } // FL UMBI is in this format 10/20 N, but BIPD is in this format 10/20/10
            return policyLimitBIPD.includes(val.split(' ')[0]);
          }),
        policyLimitUIMBI: Yup.string().test(
          'uimbiCSL',
          'UIM BI cannot be CSL unless policy UM BI and BI/PD are also CSL.',
          function () {
            const { policyLimitBIPD, policyLimitUIMBI } = this.options.context?.autoCoverage || {};
            const hasAutoPolicy = this.options.context?.auto;
            const combinedUMUIMState = combinedUMUIM[this.options.context?.state];
            if (combinedUMUIMState || !hasAutoPolicy) {
              return true;
            }
            const bipdCSL = policyLimitBIPD.includes('CSL');
            const uimbiCSL = policyLimitUIMBI.includes('CSL');
            return policyLimitUIMBI === '0/0' || bipdCSL === uimbiCSL;
          }
        ),
        policyLimitUMPD: Yup.mixed()
          .nullable()
          .test(
            'limitUMPDMustBeLowerThanPLimitValue',
            "Error, this can't be higher than property damage limit",
            function (value) {
              if (autoCoverage.skipPDValidation) {
                return true;
              }
              const state = this.options.context?.state;
              if (state === 'NM' || !policyLevelUMPDStates[state]) {
                // in NM, we just have a UMPD = PD limit option and shouldn't run this check.
                // or if we don't show UMPD on policy level, then don't run this check.
                return true;
              }
              if (policyLevelUMPDInUMSelectionStates[state]) {
                // in these states, UMPD is the third value in policyLimitUMBI (e.g. "25/50/25")
                const umbiParts = autoCoverage.policyLimitUMBI?.split('/');
                if (!umbiParts || umbiParts.length < 3) {
                  return true;
                }
                const umpdFromUMBI = Number(umbiParts[2]);
                if (Number.isNaN(umpdFromUMBI)) {
                  return true; // can't validate, skip
                }
                return umpdFromUMBI <= getPLimitValue(autoCoverage.policyLimitBIPD);
              }
              return getUMPDValue(value) <= getPLimitValue(autoCoverage.policyLimitBIPD);
            }
          )
          .test('noLimitUMPDWithoutUMBI', 'Error, UMPD cannot be chosen in this state without UMBI', function (value) {
            const state = this.options.context?.state;
            return !(
              value !== 'NONE' &&
              UMPDOnlyWithUMBIStates[state] &&
              policyLevelUMPDStates[state] &&
              (autoCoverage.policyLimitUMBI?.startsWith('0/0') || autoCoverage.policyLimitUMBI === 'NONE')
            );
          })
      }),
      cars: Yup.array(
        Yup.object().shape({
          limitUMPD: Yup.mixed()
            .nullable()
            .test(
              'limitUMPDMustBeLowerThanPLimitValue',
              "Error, this can't be higher than property damage limit",
              function (value) {
                if (autoCoverage.skipPDValidation) {
                  return true;
                }
                const state = this.options.context?.state;
                if (state === 'NM' || doNotShowCarLevelUMPDStates[state]) {
                  // in NM, we just have a UMPD = PD limit option and shouldn't run this check.
                  // or we don't have UMPD in this state or car level UMPD in this state so don't run this check
                  return true;
                }
                return getUMPDValue(value) <= getPLimitValue(autoCoverage.policyLimitBIPD);
              }
            )
            .test(
              'noLimitUMPDWithoutUMBI',
              'Error, UMPD cannot be chosen in this state without UMBI',
              function (value) {
                const state = this.options.context?.state;
                if (doNotShowCarLevelUMPDStates[state]) {
                  // we don't have UMPD in this state or car level UMPD in this state so don't run this check
                  return true;
                }
                return !(
                  value !== 'NONE' &&
                  UMPDOnlyWithUMBIStates[state] &&
                  (autoCoverage.policyLimitUMBI?.startsWith('0/0') || autoCoverage.policyLimitUMBI === 'NONE')
                );
              }
            )
            .test('Invalid', 'You may not choose UMPD Coverage when you have Collision coverage', function (value) {
              if (
                umpdWithCollisionStates.includes(this.options.context.state) ||
                doNotShowCarLevelUMPDStates[this.options.context.state]
              ) {
                return true;
              }
              if (value && value !== 'NONE') {
                const { deductibleCollision } = this.parent;
                return deductibleCollision === 'NONE';
              }
              return true;
            }),
          garageLocation: Yup.object()
            .nullable()
            .shape({
              address: Yup.string()
                .nullable()
                .test('is-po-box', 'PO Box Addresses are invalid', (value) => {
                  const pattern = new RegExp('\\b[p]*(ost)*\\.*\\s*[o|0]*(ffice)*\\.*\\s*b[o|0]x\\b', 'i');
                  return !(pattern.test(value) || pattern.test(value));
                }),
              state: Yup.string()
                .nullable()
                .validUSState('auto.garageLocation.state')
                .test('garage-state-policy-test', function (value) {
                  const [_, currentCar] = this.from;
                  const { isGarageAddressRuleExceptionApproved } = currentCar.value;
                  if (isGarageAddressRuleExceptionApproved) {
                    return true;
                  }

                  if (value !== this.options?.context.state) {
                    const isInternalOrExternalAgent = session.isInternalSales || session.isExternalSales;
                    return this.createError({
                      message: `Garage address must be in the same state as the policy.${
                        isInternalOrExternalAgent
                          ? ' If an exception needs to be made, please follow your appropriate process for escalating to a Branch underwriting representative.'
                          : ''
                      }`
                    });
                  }
                  return true;
                }),
              zip: Yup.string()
                .nullable()
                .min(5, 'Zip code must be 5 digits')
                .test('garage-zip-policy-test', function (value) {
                  const [_, currentCar] = this.from;
                  const { isGarageAddressRuleExceptionApproved } = currentCar.value;
                  if (isGarageAddressRuleExceptionApproved) {
                    return true;
                  }

                  const policyState = this.options.context?.state;
                  const isValidZipCode = checkZipCodeRange(value, policyState);
                  if (!isValidZipCode) {
                    const isInternalOrExternalAgent = session.isInternalSales || session.isExternalSales;
                    return this.createError({
                      message: `Garage address must be in the valid zip code range of the policy state.${
                        isInternalOrExternalAgent
                          ? ' If an exception needs to be made, please follow your appropriate process for escalating to a Branch underwriting representative.'
                          : ''
                      }`
                    });
                  }
                  return true;
                })
            }),
          assignedDriver: Yup.mixed().test(
            'assignedDriver',
            'A driver may only be assigned  to one car',
            function (value) {
              if (driverCarAssignmentStates[this.options.context.state] && this.options.context.state !== 'MA') {
                if (!value) {
                  return this.createError({
                    message: 'All cars must be assigned a driver'
                  });
                }
                const cars = this.options.context.cars;
                const drivers = this.options.context.drivers.filter((driver) => !driver.excludeDriver);
                const assignedDrivers = cars.map((car) => car.assignedDriver);
                const driversSet = new Set(assignedDrivers);

                if (drivers.length <= cars.length) {
                  return drivers.length === driversSet.size
                    ? true
                    : this.createError({
                        message: 'Every driver must be assigned to a car'
                      });
                }
                if (drivers.length > cars.length) {
                  return cars.length <= driversSet.size
                    ? true
                    : this.createError({
                        message: 'A driver may only be assigned to one car'
                      });
                }

                return true;
              }
              return true;
            }
          ),
          deductibleCollision: Yup.string()
            .requiredString('Collision deductible is required')
            .test('Invalid', 'To add Collision, Comprehensive coverage is required.', function (value) {
              if (this.options.context.state === 'DC') {
                if (value && value !== 'NONE') {
                  const { deductibleComprehensive } = this.parent;
                  return deductibleComprehensive !== 'NONE';
                }
              }
              return true;
            })
            .test('Invalid', 'You may not have Collision coverage when you choose UMPD Coverage', function (value) {
              if (
                umpdWithCollisionStates.includes(this.options.context.state) ||
                doNotShowCarLevelUMPDStates[this.options.context.state]
              ) {
                return true;
              }
              if (value && value !== 'NONE') {
                const { limitUMPD } = this.parent;
                return !limitUMPD || limitUMPD === 'NONE';
              }
              return true;
            }),
          deductibleComprehensive: Yup.string().requiredString('Comprehensive deductible is required'),
          limitRental: Yup.string().requiredString('Rental car expense is required'),
          limitACPE: Yup.string()
            .requiredString('Additional custom parts fields is required')
            .test('Invalid', function (value) {
              if (!isLimitACPEValid(value, this.parent)) {
                return this.createError({
                  message: `To add ${
                    this.options.context?.state === 'VA' ? 'Add On Equipment' : 'Additional Custom Parts'
                  } coverage, both Collision and Comprehensive coverages are required.`
                });
              }
              return true;
            }),
          primaryUse: Yup.string().requiredString('Please select the primary use of the vehicle').nullable(),
          symbolMake: Yup.string()
            .test(
              'symbolMake-length-is-2',
              'Make symbol must be exactly 2 characters',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually || context.state === 'FL') {
                  return true;
                }
                if (parent.symbolAux && !hasMakeModelStyleSymbols(parent)) {
                  return value && value?.length === 2;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value && value.trim().length === 2
                  : true;
              }
            )
            .nullable(),
          symbolAux: Yup.string()
            .test(
              'symbolAux-length-is-2',
              'Auxiliary symbol must be exactly 2 characters',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually || context.state === 'FL') {
                  return true;
                }
                if (hasMakeModelStyleSymbols(parent)) {
                  return value && value?.length === 2;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value && value.trim().length === 2
                  : true;
              }
            )
            .nullable(),
          symbolModel: Yup.string()
            .test(
              'symbolModel-length-is-2',
              'Model symbol must be exactly 2 characters',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually || context.state === 'FL') {
                  return true;
                }
                if (parent.symbolAux && !hasMakeModelStyleSymbols(parent)) {
                  return value && value?.length === 2;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value && value.trim().length === 2
                  : true;
              }
            )
            .nullable(),
          symbolStyle: Yup.string()
            .test(
              'symbolStyle-length-is-2',
              'Style symbol must be exactly 2 characters',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually || context.state === 'FL') {
                  return true;
                }
                if (parent.symbolAux && !hasMakeModelStyleSymbols(parent)) {
                  return value && value?.length === 2;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value && value.trim().length === 2
                  : true;
              }
            )
            .nullable(),
          symbolPGS: Yup.string()
            .test(
              'symbolPGS-length-is-1',
              'PGS symbol must be at least 1 character',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually || context.state !== 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length >= 1
                  : true;
              }
            )
            .nullable(),
          symbolBI: Yup.number()
            .test('symbolBI-length-is-required', 'BI symbol is required', (value, { options: { context, parent } }) => {
              if (!context.canAddCarsManually || context.state !== 'FL') {
                return true;
              }
              return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                ? value?.trim().length >= 0
                : true;
            })
            .nullable(),
          symbolPD: Yup.number()
            .test('symbolPD-length-is-required', 'PD symbol is required', (value, { options: { context, parent } }) => {
              if (!context.canAddCarsManually || context.state !== 'FL') {
                return true;
              }
              return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                ? value?.trim().length >= 0
                : true;
            })
            .nullable(),
          symbolMed: Yup.number()
            .test(
              'symbolMed-length-is-required',
              'Med symbol is required',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually || context.state !== 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length >= 0
                  : true;
              }
            )
            .nullable(),
          symbolPIP: Yup.number()
            .test(
              'symbolStyle-length-is-required',
              'PIP symbol is required',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually || context.state !== 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length >= 0
                  : true;
              }
            )
            .nullable(),
          symbolColl: Yup.number()
            .test(
              'symbolCOLL-length-is-required',
              'Collision symbol is required',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually || context.state !== 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length >= 0
                  : true;
              }
            )
            .nullable(),
          symbolComp: Yup.number()
            .test(
              'symbolCOMP-length-is-required',
              'Comprehensive symbol is required',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually || context.state !== 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length >= 0
                  : true;
              }
            )
            .nullable(),
          symbolUM: Yup.number()
            .test(
              'symbolUMUIM-length-is-required',
              'UM/UIM symbol is required',
              (value, { options: { context, parent } }) => {
                if (!context.canAddCarsManually || context.state !== 'FL') {
                  return true;
                }
                return haveAnyOfThisCarsSymbolsChanged(parent, context.initialValues.cars)
                  ? value?.trim().length >= 0
                  : true;
              }
            )
            .nullable()
        })
      )
        .min(
          1,
          'Please add a car before updating this policy. If there are no cars to insure, please follow your designated process to cancel the auto policy.'
        )
        .nullable(),
      trailers: Yup.array(
        Yup.object().shape({
          VIN: Yup.string().requiredString('VIN is required'),
          type: Yup.string().requiredString('Trailer type is required'),
          primaryUse: Yup.string().requiredString("Please select the trailer's primary use").nullable(),
          year: Yup.number().typeError('Trailer year should be a number').required('Trailer year is required'),
          value: Yup.number()
            .typeError('Trailer value should be a number between $0 - $50000')
            .positive('Trailer value should be between $0 - $50000')
            .integer('Trailer value should be between $0 - $50000')
            .min(1, 'Trailer value should be greater than $0')
            .max(50000, 'Trailer value should be less than or equal to $50000')
            .when(
              ['deductibleCollision', 'deductibleComprehensive'],
              (deductibleCollision, deductibleComprehensive, schema) => {
                const validValues = [deductibleCollision.split('/')[0], deductibleComprehensive.split('/')[0]].filter(
                  (value) => value !== 'NONE' && value
                );
                if (!validValues.length) {
                  return schema;
                }
                const minValue = Math.min(...validValues);
                return schema.min(500 + minValue, `Trailer value should be greater than ${500 + minValue}`);
              }
            )
            .required('Trailer value is required'),
          contents: Yup.number()
            .typeError('Content value should be a number between $0 - $5000')
            .positive('Content value should be between $0 - $5000')
            .integer('Content value should be between $0 - $5000')
            .min(0, 'Content value should be greater than $0')
            .max(5000, 'Content value should be less than or equal to $5000')
            .required('Trailer content value is required'),

          deductibleCollision: Yup.string()
            .requiredString('Collision deductible is required')
            .test(
              'minimumCollDeductibleMet',
              'Trailer deductibles must match deductibles of at least one insured vehicle, or be NO COVERAGE for liability ONLY',
              (currentTrailerDeductibleCollision, { options }) => {
                const { context, parent } = options;
                if (currentTrailerDeductibleCollision === 'NONE' && parent.deductibleComprehensive === 'NONE') {
                  return true;
                }
                const { cars } = context;
                for (const car of cars) {
                  if (
                    car.deductibleCollision === currentTrailerDeductibleCollision &&
                    car.deductibleComprehensive.split('/')[0] === parent.deductibleComprehensive.split('/')[0]
                  ) {
                    return true;
                  }
                }
                return false;
              }
            ),

          deductibleComprehensive: Yup.string()
            .requiredString('Comprehensive deductible is required')
            .test(
              'minimumCompDeductibleMet',
              'Trailer deductibles must match deductibles of at least one insured vehicle, or be NO COVERAGE for liability ONLY',
              (currentTrailerDeductibleComprehensive, { options }) => {
                const { context, parent } = options;
                if (currentTrailerDeductibleComprehensive === 'NONE' && parent.deductibleCollision === 'NONE') {
                  return true;
                }
                const { cars } = context;
                for (const car of cars) {
                  if (
                    car.deductibleComprehensive.split('/')[0] === currentTrailerDeductibleComprehensive.split('/')[0] &&
                    car.deductibleCollision === parent.deductibleCollision
                  ) {
                    return true;
                  }
                }
                return false;
              }
            ),
          garageLocation: Yup.object().shape({
            address: Yup.string()
              .requiredString('Address is required')
              .test('is-po-box', 'PO Box Addresses are invalid', (value) => {
                const pattern = new RegExp('\\b[p]*(ost)*\\.*\\s*[o|0]*(ffice)*\\.*\\s*b[o|0]x\\b', 'i');
                return !(pattern.test(value) || pattern.test(value));
              }),
            city: Yup.string().requiredString('City is required'),
            state: Yup.string().requiredString('State is required').validUSState('policy.trailers'),
            zip: Yup.string().requiredString('Zip code is required').min(5, 'Zip code must be 5 digits')
          })
        })
      ).nullable(),
      drivers: Yup.array(
        Yup.object().shape({
          excludeDriver: Yup.boolean()
            .nullable()
            .test('excludeDriver', 'Error, umbrella is active on Home policy', (value) => {
              if (value) {
                const matchedHomePolicy = getMatchedHomePolicy();
                if (matchedHomePolicy) {
                  return !matchedHomePolicy.policyDetails.includeUmbrella;
                }
              }
              return true;
            }),
          dateOfBirth: Yup.string().test('minimum-driver-age', '', function (value) {
            if (value) {
              const { index } = this.options;
              const { firstName, lastName } = this.options.context.drivers[index];
              const today = new Date();
              const birthDate = new Date(value);
              const age = differenceInYears(today, birthDate);

              if (age < 14) {
                return this.createError({
                  message: `${firstName} ${lastName} is too young to be on the policy`,
                  path: this.path
                });
              }
            }
            return true;
          }),
          driversLicenseNumber: driversLicenseNumberSchema.nullable(),
          schoolName: Yup.string()
            .when('fullTimeStudent', {
              is: (fullTimeStudent) => fullTimeStudent === true,
              then: Yup.string().ensure().addDriverNameToError('School name')
            })
            .nullable(),

          schoolLocation: Yup.object()
            .when('fullTimeStudent', {
              is: (fullTimeStudent) => fullTimeStudent === true,
              then: Yup.object().shape({
                address: Yup.string().ensure().addDriverNameToError('School address'),
                city: Yup.string().ensure().addDriverNameToError('School city'),
                state: Yup.string().ensure().addDriverNameToError('School state'),
                zip: Yup.string().ensure().addDriverNameToError('School zip code').min(5, 'Zip code must be 5 digits')
              })
            })
            .nullable(),
          driversLicenseState: Yup.string()
            .requiredString('State is required')
            .validUSState('auto.drivers.driversLicenseState'),
          assignedCar: Yup.mixed().test('assignedCar', 'Each driver must be assigned to one car', function (value) {
            if (driverCarAssignmentStates[this.options.context.state] && this.options.context.state !== 'MA') {
              if (!value) {
                return this.createError({
                  message: 'All drivers must be assigned a primary vehicle'
                });
              }
              const cars = this.options.context.cars;
              if (value && !cars.some((car) => car.VIN === value)) {
                return this.createError({
                  message: 'Driver assigned VIN that does not exist '
                });
              }
              const drivers = this.options.context.drivers.filter((driver) => !driver.excludeDriver);
              const assignedDrivers = cars.map((car) => car.assignedDriver);

              if (drivers.length === cars.length) {
                const driversSet = new Set(assignedDrivers);
                // assert that each driver assigned exactly once
                return driversSet.size === assignedDrivers.length && drivers.length === driversSet.size
                  ? true
                  : this.createError({
                      message: 'Each driver must be assigned to one car'
                    });
              }

              return true;
            }
            return true;
          }),
          userAssignedCar: Yup.mixed().test('userAssignedCar', 'This car already has a main driver', function (value) {
            if (driverCarAssignmentStates[this.options.context.state] && this.options.context.state === 'MA') {
              if (!value || value === 'NONE') {
                return true;
              }
              const cars = this.options.context.cars;
              if (value && !cars.some((car) => car.VIN === value)) {
                return this.createError({
                  message: 'Driver assigned VIN that does not exist '
                });
              }
              const inexperiencedDrivers = this.options.context.drivers.filter((driver) => {
                let age = driver.age;
                if (!age) {
                  const today = new Date();
                  const birthDate = new Date(driver.dateOfBirth);
                  age = differenceInYears(today, birthDate);
                }
                return driver.age - driver.ageFirstLicensed < inexperiencedYearsLimit[this.options.context.state];
              });

              if (inexperiencedDrivers.filter((d) => d.userAssignedCar === value).length > 1) {
                return this.createError({
                  message: 'This car already has a main driver'
                });
              }
            }
            return true;
          })
        })
      ).nullable(),
      fcraDisclaimer: Yup.boolean().test(
        'true-when-adding-coapp',
        'Must agree to FCRA disclaimer when adding co-applicant',
        function (value) {
          const newCoAppAdded = hasNewCoApplicant(
            this.options.context?.initialValues?.drivers,
            this.options.context?.drivers
          );

          if (newCoAppAdded) {
            return value;
          }

          // force pass because no coapp got added
          return true;
        }
      )
    });
  });
